
import { useState, useEffect } from 'react';
import { appsUserApiRouterRegisterView } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getBestMatch } from '@/lib/geocoding';
import TosModal from './TosModal';

interface SignUpFormProps {
  onSuccess: () => void;
  onCancel?: () => void;
}

const SignUpForm = ({ onSuccess, onCancel }: SignUpFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [ridingStyle, setRidingStyle] = useState('Street');
  const [location, setLocation] = useState('');
  const [locationPoint, setLocationPoint] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showTosModal, setShowTosModal] = useState(false);
  const { toast } = useToast();
  const { checkAuthState } = useAuth();

  const clearErrors = () => {
    setErrors({});
  };

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLocation = e.target.value;
    setLocation(newLocation);
    setGeocodingError(null);
    setLocationPoint(null);

    if (errors.location) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.location;
        return newErrors;
      });
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (newLocation.trim()) {
      const timer = setTimeout(() => {
        performGeocoding(newLocation);
      }, 1000);
      setDebounceTimer(timer);
    }
  };

  const performGeocoding = async (address: string) => {
    setIsGeocoding(true);
    setGeocodingError(null);

    try {
      const result = await getBestMatch(address);

      if (result) {
        const [longitude, latitude] = result.coordinates;
        setLocationPoint({ latitude, longitude });
        setGeocodingError(null);
      } else {
        setGeocodingError('Could not find coordinates for this location');
        setLocationPoint(null);
      }
    } catch (error) {
      setGeocodingError('Failed to geocode location');
      setLocationPoint(null);
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();

    if (!username.trim()) {
      setErrors({ username: 'Username is required' });
      return;
    }

    if (!location.trim()) {
      setErrors({ location: 'Location is required' });
      return;
    }

    if (!locationPoint) {
      setErrors({ location: 'Please wait for location to be geocoded or enter a valid location' });
      return;
    }

    // Show TOS modal instead of calling API directly
    setShowTosModal(true);
  };

  const handleTosAccept = async () => {
    setShowTosModal(false);
    setLoading(true);

    try {
      const response = await appsUserApiRouterRegisterView({
        email,
        password,
        password_confirm: password,
        username,
        preferred_riding_style: ridingStyle,
        location: location.trim(),
        location_point: locationPoint as { latitude: number; longitude: number },
        tos_accepted: true,
      });
      const { user, message } = response.data;

      if (!user) {
        console.error("Auth signup error:", message);
        throw new Error(message);
      }

      // Refresh auth state to update the context with the new session
      await checkAuthState();

      toast({
        title: "Account created",
        description: "Your account has been created successfully. Please check your email for verification.",
      });

      onSuccess();
    } catch (error: any) {
      console.error("Registration error details:", error);

      // Parse error message to determine field-specific errors
      const errorMessage = error.message || 'An error occurred during registration';

      if (errorMessage.toLowerCase().includes('email')) {
        setErrors({ email: errorMessage });
      } else if (errorMessage.toLowerCase().includes('password')) {
        setErrors({ password: errorMessage });
      } else if (errorMessage.toLowerCase().includes('username')) {
        setErrors({ username: errorMessage });
      } else if (errorMessage.toLowerCase().includes('location')) {
        setErrors({ location: errorMessage });
      } else {
        setErrors({ general: errorMessage });
      }

      toast({
        title: "Registration failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTosCancel = () => {
    setShowTosModal(false);
    onCancel?.();
  };

  return (
    <>
      <form onSubmit={handleSignUp} className="space-y-4">
        {errors.general && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
            {errors.general}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">
            Username
          </label>
          <Input
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (errors.username) clearErrors();
            }}
            placeholder="adventurerider"
            className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all ${
              errors.username ? 'border-red-300 focus:ring-red-500' : 'border-adv-darkGray'
            }`}
            required
          />
          {errors.username && (
            <p className="text-sm text-red-600 mt-1">{errors.username}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Preferred Riding Style
          </label>
          <Select
            value={ridingStyle}
            onValueChange={setRidingStyle}
          >
            <SelectTrigger className="w-full px-4 py-2.5 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all">
              <SelectValue placeholder="Select riding style" />
            </SelectTrigger>
            <SelectContent className="border border-adv-darkGray">
              <SelectItem value="Street">Street</SelectItem>
              <SelectItem value="Seeking Off-road">Seeking Off-road</SelectItem>
              <SelectItem value="Fully Off-road">Fully Off-road</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Home Base
          </label>
          <Input
            type="text"
            value={location}
            onChange={handleLocationChange}
            placeholder="e.g., Nashville, TN, USA"
            className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all ${
              errors.location ? 'border-red-300 focus:ring-red-500' : 'border-adv-darkGray'
            }`}
            required
          />
          {isGeocoding && (
            <p className="text-xs text-adv-lightGray mt-1">
              Geocoding location...
            </p>
          )}
          {geocodingError && !isGeocoding && (
            <p className="text-sm text-red-600 mt-1">{geocodingError}</p>
          )}
          {locationPoint && !isGeocoding && (
            <p className="text-xs text-green-600 mt-1">
              Location verified
            </p>
          )}
          {errors.location && (
            <p className="text-sm text-red-600 mt-1">{errors.location}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Email
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) clearErrors();
            }}
            placeholder="yourname@example.com"
            className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all ${
              errors.email ? 'border-red-300 focus:ring-red-500' : 'border-adv-darkGray'
            }`}
            required
          />
          {errors.email && (
            <p className="text-sm text-red-600 mt-1">{errors.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Password
          </label>
          <Input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) clearErrors();
            }}
            placeholder="••••••••"
            className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all ${
              errors.password ? 'border-red-300 focus:ring-red-500' : 'border-adv-darkGray'
            }`}
            required
          />
          {errors.password && (
            <p className="text-sm text-red-600 mt-1">{errors.password}</p>
          )}
          <p className="text-xs text-adv-lightGray mt-1">
            Must be at least 8 characters
          </p>
        </div>

        <Button
          type="submit"
          className="w-full btn-primary"
          disabled={loading}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </form>

      <TosModal
        isOpen={showTosModal}
        onAccept={handleTosAccept}
        onCancel={handleTosCancel}
        loading={loading}
      />
    </>
  );
};

export default SignUpForm;
